import type {
  CompetitionField,
  CompetitionRow,
  CompetitionSort,
  CompetitionUnavailableReason,
} from '@/api/competitions'
import { Badge, formatInteger, formatPercent, formatRatio } from '@/design'

import { day, formatMinor, money, nullableCells, UNDEFINED, type ListCellContext } from '../list/cells'
import type { DescribeReason, ListColumn } from '../list/nullState'
import { unavailableReason } from './nullState'

/** What a column's cells need beyond the row: the shared list context. Every money column but the ticket price is in its currency. */
export type CellContext = ListCellContext<CompetitionRow, CompetitionUnavailableReason>

export type ColumnGroup = 'identity' | 'commerce' | 'behaviour' | 'advertising'

export const GROUP_LABELS: Record<ColumnGroup, string> = {
  identity: 'Competition',
  commerce: 'Sales',
  behaviour: 'Measured behaviour',
  advertising: 'Advertising',
}

export interface CompetitionColumn extends ListColumn<CompetitionRow, CellContext> {
  group: ColumnGroup
  field?: CompetitionField
  sort?: CompetitionSort
}

/** Why a column is empty, in the words a tooltip and the page's note use. */
export const REASON_TEXT: Record<CompetitionUnavailableReason, string> = {
  needs_ad_platform: 'Needs a connected ad platform: no ad spend reaches Competition Insights yet.',
  before_first_event: 'Browser tracking had not started on this website by the end of the period.',
  refund_not_itemised:
    'A refund in the period was on an order that held more than this competition, so it cannot be placed on it.',
}

/** How the competitions list words a reason: every figure the API could not give is "Not live yet". */
export const describeReason: DescribeReason<CompetitionUnavailableReason> = (reason) => ({
  label: 'Not live yet',
  why: REASON_TEXT[reason],
})

const { figure } = nullableCells<CompetitionRow, CompetitionUnavailableReason, CellContext>(unavailableReason, describeReason)

/** A status word as a label: "live" → "Live"; a blank one says so. */
export function statusLabel(status: string): string {
  const word = status.trim()
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : 'No status'
}

/** A fraction sold as a bar and a percentage: the list's % sold cell, and the competition page's header. */
export function SellThrough({ value }: { value: number }) {
  return (
    <span className="cmp-sell">
      <span className="cmp-sell__track" aria-hidden="true">
        <span className="cmp-sell__bar" style={{ width: `${Math.min(100, Math.max(0, value * 100))}%` }} />
      </span>
      {formatPercent(value, 0)}
    </span>
  )
}

const count = (value: number) => formatInteger(value)

/** Every column the list can show, in display order. The first is always shown. */
export const COMPETITION_COLUMNS: readonly CompetitionColumn[] = [
  {
    key: 'name',
    label: 'Competition',
    group: 'identity',
    sort: 'name',
    byDefault: true,
    cell: (row, context) => {
      const detail = [row.category, row.endAt ? `closes ${day(row.endAt, context)}` : null].filter(Boolean).join(' · ')
      return (
        <span className="list-cell-stack">
          <span>{row.name}</span>
          {detail ? <span className="list-cell-sub">{detail}</span> : null}
        </span>
      )
    },
  },
  {
    key: 'status',
    label: 'Status',
    group: 'identity',
    byDefault: true,
    cell: (row) => <Badge tone={row.active ? 'positive' : 'neutral'}>{statusLabel(row.status)}</Badge>,
  },
  {
    key: 'revenue',
    label: 'Revenue',
    group: 'commerce',
    sort: 'revenue',
    byDefault: true,
    align: 'end',
    cell: (row, context) => money(row.revenueGrossMinor, context),
  },
  {
    key: 'orders',
    label: 'Orders',
    group: 'commerce',
    sort: 'orders',
    byDefault: true,
    align: 'end',
    cell: (row) => count(row.orders),
  },
  {
    key: 'tickets_sold_in_range',
    label: 'Tickets sold',
    group: 'commerce',
    sort: 'tickets_sold_in_range',
    byDefault: true,
    align: 'end',
    cell: (row) => count(row.ticketsSoldInRange),
  },
  {
    key: 'sell_through',
    label: '% sold',
    group: 'commerce',
    field: 'sellThrough',
    sort: 'sell_through',
    byDefault: true,
    align: 'end',
    cell: figure('sellThrough', (row) => row.sellThrough, (value) => <SellThrough value={value} />),
  },
  {
    key: 'conversion_rate',
    label: 'Conversion',
    group: 'behaviour',
    field: 'conversionRate',
    sort: 'conversion_rate',
    byDefault: true,
    align: 'end',
    cell: figure('conversionRate', (row) => row.conversionRate, (value) => formatPercent(value)),
  },
  {
    key: 'views',
    label: 'Views',
    group: 'behaviour',
    field: 'views',
    sort: 'views',
    byDefault: true,
    align: 'end',
    cell: figure('views', (row) => row.views, count),
  },
  {
    key: 'revenue_net',
    label: 'Net revenue',
    group: 'commerce',
    field: 'revenueNetMinor',
    sort: 'revenue_net',
    byDefault: false,
    align: 'end',
    cell: figure('revenueNetMinor', (row) => row.revenueNetMinor, money),
  },
  {
    key: 'aov',
    label: 'Average order',
    group: 'commerce',
    field: 'aovMinor',
    sort: 'aov',
    byDefault: false,
    align: 'end',
    cell: figure('aovMinor', (row) => row.aovMinor, money),
  },
  {
    key: 'buyers',
    label: 'Buyers',
    group: 'commerce',
    sort: 'buyers',
    byDefault: false,
    align: 'end',
    cell: (row) => count(row.buyers),
  },
  {
    key: 'new_customers',
    label: 'New customers',
    group: 'commerce',
    sort: 'new_customers',
    byDefault: false,
    align: 'end',
    cell: (row) => count(row.newCustomers),
  },
  {
    key: 'returning_customers',
    label: 'Returning customers',
    group: 'commerce',
    sort: 'returning_customers',
    byDefault: false,
    align: 'end',
    cell: (row) => count(row.returningCustomers),
  },
  {
    key: 'tickets_sold_lifetime',
    label: 'Tickets sold to date',
    group: 'commerce',
    field: 'ticketsSoldLifetime',
    sort: 'tickets_sold_lifetime',
    byDefault: false,
    align: 'end',
    cell: figure('ticketsSoldLifetime', (row) => row.ticketsSoldLifetime, count),
  },
  {
    key: 'total_ticket_capacity',
    label: 'Capacity',
    group: 'identity',
    field: 'totalTicketCapacity',
    sort: 'total_ticket_capacity',
    byDefault: false,
    align: 'end',
    cell: figure('totalTicketCapacity', (row) => row.totalTicketCapacity, count),
  },
  {
    key: 'ticket_price',
    label: 'Ticket price',
    group: 'identity',
    byDefault: false,
    align: 'end',
    // In the competition's own currency, not converted: the API has no sort on it for that reason.
    cell: (row, { locale }) =>
      row.ticketPriceMinor === null ? UNDEFINED : formatMinor(row.ticketPriceMinor, row.ticketPriceCurrency, locale),
  },
  {
    key: 'category',
    label: 'Category',
    group: 'identity',
    byDefault: false,
    cell: (row) => row.category ?? UNDEFINED,
  },
  {
    key: 'end_at',
    label: 'Closes',
    group: 'identity',
    field: 'endAt',
    sort: 'end_at',
    byDefault: false,
    cell: figure('endAt', (row) => row.endAt, (value, context) => day(value, context)),
  },
  {
    key: 'unique_viewers',
    label: 'Unique viewers',
    group: 'behaviour',
    field: 'uniqueViewers',
    sort: 'unique_viewers',
    byDefault: false,
    align: 'end',
    cell: figure('uniqueViewers', (row) => row.uniqueViewers, count),
  },
  {
    key: 'selector_opens',
    label: 'Selector opens',
    group: 'behaviour',
    field: 'selectorOpens',
    sort: 'selector_opens',
    byDefault: false,
    align: 'end',
    cell: figure('selectorOpens', (row) => row.selectorOpens, count),
  },
  {
    key: 'tickets_selected',
    label: 'Tickets selected',
    group: 'behaviour',
    field: 'ticketsSelected',
    sort: 'tickets_selected',
    byDefault: false,
    align: 'end',
    cell: figure('ticketsSelected', (row) => row.ticketsSelected, count),
  },
  {
    key: 'add_to_baskets',
    label: 'Add to baskets',
    group: 'behaviour',
    field: 'addToBaskets',
    sort: 'add_to_baskets',
    byDefault: false,
    align: 'end',
    cell: figure('addToBaskets', (row) => row.addToBaskets, count),
  },
  {
    key: 'ad_spend',
    label: 'Ad spend',
    group: 'advertising',
    field: 'adSpendMinor',
    byDefault: false,
    align: 'end',
    cell: figure('adSpendMinor', (row) => row.adSpendMinor, money),
  },
  {
    key: 'attributed_revenue',
    label: 'Attributed revenue',
    group: 'advertising',
    field: 'attributedRevenueMinor',
    byDefault: false,
    align: 'end',
    cell: figure('attributedRevenueMinor', (row) => row.attributedRevenueMinor, money),
  },
  {
    key: 'cpa',
    label: 'CPA',
    group: 'advertising',
    field: 'cpaMinor',
    byDefault: false,
    align: 'end',
    cell: figure('cpaMinor', (row) => row.cpaMinor, money),
  },
  {
    key: 'roas',
    label: 'ROAS',
    group: 'advertising',
    field: 'roas',
    byDefault: false,
    align: 'end',
    cell: figure('roas', (row) => row.roas, (value) => formatRatio(value)),
  },
]

export const COLUMN_KEYS: readonly string[] = COMPETITION_COLUMNS.map((column) => column.key)
export const DEFAULT_COLUMN_KEYS: readonly string[] = COMPETITION_COLUMNS.filter((column) => column.byDefault).map(
  (column) => column.key,
)
